import React from 'react';
import { ReactComponent as LockIcon } from '@repo/common/assets/icons/16px/icon_16px_lock_outline.svg';
import { ReactComponent as ChapterIcon } from '@repo/common/assets/icons/20px/icon_20px_clipboard_outline.svg';
import { JOB_LABEL, cn, initials } from '../model/derive';
import { formatHm } from '../model/time';
import { STATUS_CHIP, kstClock, kstDue } from '../model/completeProject';
import { StatusChip } from '../primitives';
import TdButton from '../Button';
import { Alert, Card, CardTitle, Footer, LeaderBadge } from './ui';

/** Complete project 허브·잠김 화면 */

export const LockedView = ({ endLabel, onBack }) => (
  <main className='flex flex-1 items-start justify-center px-4 pt-5 md:items-center md:p-10'>
    <div className='flex w-full max-w-[480px] flex-col items-center gap-4 rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 px-7 py-10 text-center shadow-4'>
      <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-surface-green10'>
        <LockIcon className='h-5 w-5 text-green-deep02' />
      </span>
      <div className='flex flex-col gap-1.5'>
        <h1 className='text-[20px] font-medium leading-[130%] text-text-primary'>
          Complete project isn’t open yet
        </h1>
        <p className='text-[14px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
          It opens when the leader submits the final deliverable, or when the project ends ·{' '}
          {endLabel}.
        </p>
      </div>
      <TdButton variant='soft' size='l' onClick={onBack} className='px-5'>
        Back to workboard
      </TdButton>
    </div>
  </main>
);

const STEP_STYLE = [
  { bar: 'bg-green-deep02', dim: 'bg-green-deep03/30', bg: 'bg-surface-green10' },
  {
    bar: 'bg-functional-warning03',
    dim: 'bg-functional-warning03/30',
    bg: 'bg-functional-warning01',
  },
  { bar: 'bg-functional-failed03', dim: 'bg-functional-failed03/30', bg: 'bg-functional-failed01' },
];

const Timeline = ({ cp }) => {
  const steps = [
    {
      label: 'On time',
      time: `Until ${kstDue(cp.dueAt)}`,
      desc: 'Within 3 working hours after the final submission. Offline hours don’t count.',
    },
    {
      label: 'Late',
      time: `Until ${kstDue(cp.closesAt)}`,
      desc: 'Still counts as completed. Your report shows “Peer review submitted late” and Kit reference sentences can’t be used.',
    },
    {
      label: 'Closed',
      time: `After ${kstDue(cp.closesAt)}`,
      desc: 'Not completed. No report and no certificate.',
    },
  ];
  return (
    <div role='list' aria-label='Peer review deadline' className='flex flex-col gap-2 md:flex-row'>
      {steps.map((st, i) => {
        const cur = i === cp.step;
        const s = STEP_STYLE[i];
        return (
          <div
            key={st.label}
            role='listitem'
            aria-current={cur ? 'step' : undefined}
            className={cn(
              'flex min-w-0 flex-1 flex-row gap-3 rounded-xl px-3.5 py-3 md:flex-col md:px-4 md:py-3.5',
              cur ? s.bg : 'bg-grayscale-gray00 shadow-[inset_0_0_0_1px_rgba(0,158,79,0.12)]',
            )}
          >
            <span
              aria-hidden
              className={cn(
                'w-1 shrink-0 self-stretch rounded-sm md:h-1 md:w-full',
                cur ? s.bar : s.dim,
              )}
            />
            <div className='flex min-w-0 flex-col gap-1'>
              <div className='flex flex-wrap items-center gap-2'>
                <span
                  className={cn(
                    'text-[14px] font-medium leading-[140%]',
                    i < cp.step ? 'text-text-secondary' : 'text-text-primary',
                  )}
                >
                  {st.label}
                </span>
                {cur && (
                  <span className='inline-flex h-5 items-center rounded-md bg-grayscale-gray00 px-[7px] text-[11px] font-medium text-text-primary shadow-[inset_0_0_0_1px_#d5d2cd]'>
                    {cp.kind === 'done' ? 'Submitted' : 'Now'}
                  </span>
                )}
              </div>
              <span
                className={cn(
                  'text-[14px] font-medium leading-[140%] tabular-nums',
                  i < cp.step ? 'text-text-secondary' : 'text-text-primary',
                )}
              >
                {st.time}
              </span>
              <span className='text-[13px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
                {st.desc}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Clock = ({ cp }) => {
  let big = '';
  let bigClass = 'text-text-primary';
  let sub = '';
  let tag = null;
  if (cp.kind === 'open' && cp.step === 0) big = `${formatHm(cp.leftMin)} of working time left`;
  if (cp.kind === 'open' && cp.step === 1) {
    big = `Late · ${formatHm(cp.lateLeftMin)} left to submit`;
    bigClass = 'text-functional-warning04';
    sub = `Working time ended ${kstDue(cp.dueAt)}. You can still edit every chapter.`;
  }
  if (cp.kind === 'done') {
    big = `Submitted ${kstDue(cp.submittedAt)}`;
    tag = cp.late
      ? { label: 'Submitted late', tone: 'warning' }
      : { label: 'On time', tone: 'success' };
    sub = cp.late
      ? 'You’ve completed the project. Your report shows “Peer review submitted late” and Kit reference sentences can’t be used.'
      : 'You’ve completed the project. Read-only from now on.';
  }
  if (cp.kind === 'closed') {
    big = 'Peer review closed';
    sub = `Closed ${kstDue(cp.closesAt)} without your submission, so the project isn’t completed for you.`;
  }
  return (
    <div className='flex flex-col gap-1.5'>
      <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
        <span
          className={cn(
            'text-[20px] font-medium leading-[130%] tabular-nums md:text-[24px]',
            bigClass,
          )}
        >
          {big}
        </span>
        {tag && <StatusChip chip={tag} />}
      </div>
      {cp.paused && (
        <div className='flex items-center gap-2 text-[14px] font-medium leading-[150%] text-text-tertiary'>
          <span
            aria-hidden
            className='h-2 w-2 shrink-0 rounded-full bg-grayscale-gray00 shadow-[inset_0_0_0_2px_#908d88]'
          />
          Paused while you’re offline · resumes at {kstClock(cp.resumesAt)}
        </div>
      )}
      {sub && (
        <span className='text-[14px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
          {sub}
        </span>
      )}
    </div>
  );
};

const ChapterRow = ({ first, avatar, name, leader, sub, chip, button, onOpen }) => (
  <div
    className={cn(
      'flex min-h-16 items-center gap-3 px-4 py-3 hover:bg-green-deep03/[0.03] md:min-h-[60px]',
      !first && 'border-t border-green-deep03/[0.12]',
    )}
  >
    {avatar}
    <div className='flex min-w-0 flex-1 flex-col gap-1'>
      <div className='flex flex-wrap items-center gap-1.5'>
        <span className='text-[14px] font-medium leading-[140%] text-text-primary'>{name}</span>
        {leader && <LeaderBadge />}
      </div>
      <span className='text-[13px] leading-[140%] text-text-secondary'>{sub}</span>
      <span className='self-start md:hidden'>
        <StatusChip chip={chip} />
      </span>
    </div>
    <span className='hidden shrink-0 md:inline-flex'>
      <StatusChip chip={chip} />
    </span>
    {button && (
      <TdButton variant='soft' onClick={onOpen} className='h-11 shrink-0 px-4 md:h-9'>
        {button}
      </TdButton>
    )}
  </div>
);

const ListBox = ({ children }) => (
  <div className='flex flex-col overflow-hidden rounded-xl border border-green-deep03/[0.12]'>
    {children}
  </div>
);

const GroupLabel = ({ children }) => (
  <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>{children}</span>
);

export const HubView = ({
  cp,
  project,
  mates,
  statusOf,
  retroSt,
  kitSt,
  editable,
  savedText,
  saveError,
  loadFailed,
  onOpen,
  onSubmit,
}) => {
  const ro = cp.kind === 'done';
  const total = mates.length;
  const doneN = mates.filter((m) => statusOf(m) === 'done').length;
  const can = editable && doneN === total && kitSt === 'done';
  const left = [];
  if (doneN < total) left.push(`${total - doneN} teammate chapter${total - doneN > 1 ? 's' : ''}`);
  if (kitSt !== 'done') left.push('Kit items');
  const btnLabel = (k) => (ro ? 'View' : k === 'none' ? 'Start' : 'Open');
  const lead =
    cp.kind === 'open'
      ? 'Write a chapter for each teammate, pick your Kit items, then submit all.'
      : ro
        ? 'What you submitted.'
        : '';
  const opened = cp.reset
    ? `Restarted ${kstDue(cp.windowStartAt)} · ${cp.byWho} again`
    : `Opened ${kstDue(cp.opensAt)} · ${cp.byWho}`;

  // 실제 이동 순서(Next)와 같게 — Retrospective(선택) → Kit items(제출 전 마지막 챕터)
  const optRows = [
    { key: 'retro', name: 'Retrospective', sub: 'Optional', k: retroSt },
    { key: 'kit', name: 'Kit items', sub: 'Required · picking none is fine', k: kitSt },
  ];

  return (
    <main className='flex flex-1 justify-center px-4 pt-5 md:px-10 md:pb-16 md:pt-10'>
      <div className='flex w-full max-w-[760px] flex-col gap-4 md:gap-6'>
        <div className='flex flex-col gap-1.5'>
          <span className='text-[13px] leading-[140%] text-text-secondary'>
            {project.scenario || project.briefTitle || project.title} · {project.teamName}
          </span>
          <h1 className='text-[24px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary md:text-[28px]'>
            Complete project
          </h1>
          {lead && <p className='text-[15px] leading-[150%] text-text-secondary'>{lead}</p>}
        </div>

        {cp.reset && cp.kind === 'open' && (
          <Alert
            role='status'
            title='The deadline moved because the final deliverable was resubmitted.'
          >
            Your 3-hour window restarted at {kstDue(cp.windowStartAt)}.
          </Alert>
        )}
        {loadFailed && (
          <p className='rounded-xl bg-functional-failed01 px-4 py-3 text-[13px] text-functional-failed04'>
            Couldn’t load your saved chapters. Refresh the page before you continue.
          </p>
        )}

        <Card>
          <Clock cp={cp} />
          <Timeline cp={cp} />
          <span className='text-[13px] leading-[140%] text-text-secondary tabular-nums'>
            {opened}
          </span>
        </Card>

        {cp.kind !== 'closed' && (
          <Card>
            <div className='flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1'>
              <CardTitle>Chapters</CardTitle>
              <span className='text-[13px] leading-[140%] text-text-secondary tabular-nums'>
                {doneN} of {total} teammates done
              </span>
            </div>
            <div className='flex flex-col gap-2'>
              <GroupLabel>Teammates · required</GroupLabel>
              <ListBox>
                {mates.map((m, i) => {
                  const k = statusOf(m);
                  return (
                    <ChapterRow
                      key={m.id}
                      first={i === 0}
                      avatar={
                        <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-green10 text-[12px] font-medium text-green-deep04'>
                          {initials(m.name)}
                        </span>
                      }
                      name={m.name}
                      leader={m.role === 'leader'}
                      sub={JOB_LABEL[m.job] ?? m.job}
                      chip={STATUS_CHIP[k]}
                      button={btnLabel(k)}
                      onOpen={() => onOpen(m.id)}
                    />
                  );
                })}
                {total === 0 && (
                  <p className='px-4 py-4 text-[13px] text-text-secondary'>
                    No active teammates to review.
                  </p>
                )}
              </ListBox>
            </div>
            <div className='flex flex-col gap-2'>
              <GroupLabel>Your chapters</GroupLabel>
              <ListBox>
                {optRows.map((r, i) => {
                  const notAdded = ro && r.k === 'none';
                  return (
                    <ChapterRow
                      key={r.key}
                      first={i === 0}
                      avatar={
                        <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-green-deep03/[0.06]'>
                          <ChapterIcon className='h-[18px] w-[18px] text-icon-secondary' />
                        </span>
                      }
                      name={r.name}
                      sub={r.sub}
                      chip={
                        notAdded ? { ...STATUS_CHIP.none, label: 'Not added' } : STATUS_CHIP[r.k]
                      }
                      button={notAdded ? null : btnLabel(r.k)}
                      onOpen={() => onOpen(r.key)}
                    />
                  );
                })}
              </ListBox>
            </div>
          </Card>
        )}

        {cp.kind === 'open' && (
          <Footer>
            <div className='flex flex-col gap-0.5'>
              <span
                className={cn(
                  'text-[13px] leading-[140%] tabular-nums',
                  saveError ? 'text-text-failed' : 'text-text-secondary',
                )}
              >
                {saveError ? 'Couldn’t save. Check your connection.' : savedText}
              </span>
              {!can && editable && (
                <span className='text-[13px] leading-[140%] text-text-secondary'>
                  Finish {left.join(' and ')} to submit
                </span>
              )}
            </div>
            <TdButton size='l' disabled={!can} onClick={onSubmit} className='w-full md:w-auto'>
              Submit all
            </TdButton>
          </Footer>
        )}
        <span aria-hidden className='h-1 md:hidden' />
      </div>
    </main>
  );
};

export const submitSummary = ({ mates, statusOf, retroSt, picks }) => [
  ['Teammates', `${mates.filter((m) => statusOf(m) === 'done').length} of ${mates.length} done`],
  ['Retrospective', retroSt === 'none' ? 'No' : 'Yes'],
  ['Kit items', picks ? `${picks} picked` : 'None kept'],
];
